/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import $ from 'jquery';
import {
  camelCase,
  cloneDeep,
  cloneDeepWith,
  find,
  forOwn,
  intersection,
  isArray,
  isObject,
  isPlainObject,
  map,
  snakeCase,
} from 'lodash';
import { format, parseISO } from 'date-fns';
import { ZenResponse } from './client/client';

/**
 * Returns array of objects in order specified by keys in 'order' array.
 * Ignores objects whose key property value is not provided in 'order' array.
 *
 * @param array  Array of objects, i.e.: [{ key: 1, value: 'a' }, { key: 2, value: 'b' }, { key: 3, value: 'c' }]
 * @param order  Array of strings to order by, i.e. ['c', 'b', 'a']
 * @param key    Key on which value to filter by, i.e.: 'value'
 */
export const mapOrder = (array: object[], order: string[], key: string) => {
  // Only map for order keys for which objects in array exist
  const orderSafe = intersection(
    order,
    map(array, el => el[key])
  );

  return map(orderSafe, attr => find(array, [key, attr]));
};

// Does a bit of magic to convert a string to a file and downloads it. Could
// be cleaner with some newer APIs, but because we want to specify a custom
// filename, the trick with link insertion is needed.
export const downloadFile = (contents: string, type: string, name: string) => {
  const BOM_MARKER = '\ufeff';

  // Manually add BOM marker. Currently only the Export page requires
  // this functionality. The reason for adding it manually on the frontend is that the
  // JSON Data Interchange Format does not allow for this, so the browsers silently
  // strips the marker (see: https://tools.ietf.org/html/rfc7158#section-8.1).
  // Skip for binary files.
  if (type != 'application/zip') {
    contents = BOM_MARKER + contents;
  }

  if (contents && navigator.msSaveBlob) {
    navigator.msSaveBlob(new Blob([contents], { type }), name);
    return;
  }

  const a = $('<a style="display: none;"/>');
  const url = window.URL.createObjectURL(new Blob([contents], { type }));

  a.attr({
    href: url,
    download: name,
  });

  $('body').append(a);
  a[0].click();
  window.URL.revokeObjectURL(url);
  a.remove();
};

const deepMapKeys = (obj: object | any[], fn: Function) => {
  if (!isObject(obj)) {
    // If the input is a primitive, just return it
    return obj;
  }
  const x: { [key: string]: any } = isPlainObject(obj) ? {} : [];

  forOwn(obj, function(value: object, key: string) {
    let tempValue = <any>value;
    if (isPlainObject(tempValue)) {
      tempValue = deepMapKeys(tempValue, fn);
    } else if (isArray(tempValue)) {
      tempValue = tempValue.map(val => (isPlainObject(val) ? deepMapKeys(val, fn) : val));
    }
    x[fn(key)] = tempValue as any;
  });

  return x;
};

// Converts all keys in an object to camel/snake case.
export const keysToCamel = (obj: object) => deepMapKeys(obj, camelCase);
export const keysToSnake = (obj: object) => deepMapKeys(obj, snakeCase);

// Checks if file is really an image by reading the binary contents
const VALID_HEADERS = [
  '89504e47', // png
  '47494638', // gif
  'ffd8ffe0', // jpeg
  'ffd8ffe1',
  'ffd8ffe2',
  'ffd8ffe3',
  'ffd8ffe8',
];

export const verifyImageHeader = (file: File): Promise<boolean> =>
  new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onloadend = () => {
      const arr = new Uint8Array(fileReader.result as ArrayBuffer).subarray(0, 4);
      let header = '';
      for (let i = 0; i < arr.length; i++) {
        header += arr[i].toString(16);
      }

      VALID_HEADERS.indexOf(header) > -1 ? resolve() : reject();
    };
    fileReader.readAsArrayBuffer(file);
  });

export const previewImage = (file: File): Promise<boolean> =>
  new Promise((resolve, reject) => {
    try {
      const fileReader = new FileReader();
      fileReader.onloadend = () => {
        resolve(fileReader.result);
      };
      fileReader.readAsDataURL(file);
    } catch (error) {
      reject(error);
    }
  });

export const fileToBase64 = (file: File): Promise<string> =>
  new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.readAsDataURL(file);
  });

// Modifies query parameters - to be used with URLSearchParams
export const getQueryParams = () => {
  return window.location.search;
};

export const setQueryParams = (newParams: string) => {
  window.history.replaceState(window.history.state, document.title, window.location.pathname + '?' + newParams);
};

export const redirect = (url: string) => {
  window.location.href = url;
};
export const reload = () => {
  window.location.reload();
};

export const dateDisplay = (date: String) => {
  let isoDate = parseISO(date + 'Z');
  return format(isoDate, 'MMMM d, yyyy h:mm a');
};

export const omitDeep = function(collection: any, excludeKeys: any) {
  if (!Array.isArray(excludeKeys)) {
    excludeKeys = [excludeKeys];
  }
  return cloneDeepWith(collection, prop => {
    if (!prop || typeof prop !== 'object') return;
    excludeKeys.forEach((key: string) => {
      delete prop[key];
    });
  });
};

export const matchIds = function(ids: any, items: Array<Object>, prop = 'id', clones = false) {
  // Returns: Converts array of ids into array of matching objects
  // ids: Single id or Array of ids
  // items: Array of items that should match with ids
  // clones
  if (!ids) return;

  let isArray = Array.isArray(ids);
  if (!isArray) {
    ids = [ids];
  }
  let result = ids.map(id => items.find(o => o[prop] == id));
  result = result.filter(o => Boolean(o));

  if (clones) {
    result = result.map(o => (typeof o === 'object' ? cloneDeep(o) : o));
  }
  return isArray ? result : result[0];
};

// Vuex utils
// from https://github.com/vuejs/vuex/pull/1121

export interface MapGetters<Getters> {
  <Key extends keyof Getters>(map: Key[]): { [K in Key]: () => Getters[K] };
  <Map extends Record<string, keyof Getters>>(map: Map): { [K in keyof Map]: () => Getters[Map[K]] };
}

export interface MapActions<Actions> {
  <Key extends keyof Actions>(map: Key[]): { [K in Key]: Actions[K] };
  <Map extends Record<string, keyof Actions>>(map: Map): { [K in keyof Map]: Actions[Map[K]] };
}

// TODO: better document this, or fix underlying cause
export function fixStoreElement(obj: any) {
  // we need this because
  // class Foo { bar() }
  // Object.keys(Foo.prototype) => []
  // but we need ['bar']
  const newObj: any = {};

  Object.getOwnPropertyNames(obj)
    .filter(name => name !== 'constructor')
    .forEach(name => {
      newObj[name] = obj[name];
    });

  return newObj;
}

export const objectStatusColor = (status: string): string | undefined => {
  const STATUS_COLORS: { [color: string]: string[] } = {
    light: ['Open', 'Draft', 'Added', 'Identified'],
    warning: [
      'In progress',
      'Assigned',
      'Ineffective',
      'Submitted',
      'In Scope',
      'Not in Scope',
      'Launched',
      'Not Launched',
      'Pending Assessment',
    ],
    success: ['Final', 'Effective', 'Accepted', 'Completed', 'Resolved', 'Transferred', 'Avoided'],
    dark: ['Deprecated', 'Rejected', 'Ignored/Invalid', 'Done', 'Closed'],
    primary: ['Remediation in progress'],
    'light-purple': ['Under assessment'],
    purple: ['Assessed', 'Reviewed', 'Sent'],
    error: ['Remediate'],
    secondary: ['In remediation'],
    'light-blue': ['Accepting', 'Transferring', 'Avoiding'],
    brown: ['Unfounded'],
  };

  let variant;

  Object.keys(STATUS_COLORS).forEach((color: string) => {
    if (STATUS_COLORS[color].includes(status)) {
      variant = color;
    }
  });

  return variant;
};

export function filenameFromResponse(response: ZenResponse): string {
  return response.headers['content-disposition']
    .split('; filename=')[1]
    .split('"')
    .join('');
}
